import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Trash2, Plus, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { imgUrl } from "@/context/CartContext";
import { DEFAULT_HOME, mergeHome } from "@/lib/homeContent";
import { DEFAULT_ABOUT, DEFAULT_PAGES, DEFAULT_SITE, mergeContent } from "@/lib/siteContent";

const PAGES = {
  homepage: { label: "Homepage", api: "homepage", defaults: DEFAULT_HOME, previewTo: "/?preview=1",
    sections: [
      { key: "hero", label: "Opening Section (hero photo: use Upload to replace)" }, { key: "concerns", label: "What Brings You to KISSED" },
      { key: "trust", label: "Practitioner (bio & credentials are edited on the About tab)" }, { key: "closing", label: "Final Call To Action" },
    ] },
  about: { label: "About Page", api: "about", defaults: DEFAULT_ABOUT, previewTo: "/about?preview=1",
    sections: [{ key: "hero", label: "Hero" }, { key: "founder", label: "Founder / Bio" }, { key: "credentials", label: "Credentials" }, { key: "cta", label: "Call To Action" }] },
  pages: { label: "Public Page Headers", api: "pages", defaults: DEFAULT_PAGES, previewTo: "/in-clinic-services?preview=1",
    sections: [{ key: "services", label: "Services Page" }, { key: "programs", label: "Programs Page" }, { key: "shop", label: "Shop Page" }] },
  site: { label: "Navigation & Footer", api: "site", defaults: DEFAULT_SITE, previewTo: "/?preview=1",
    sections: [{ key: "nav", label: "Navigation" }, { key: "footer", label: "Footer" }] },
};

const input = "mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm";
const isImgKey = (k) => /img|image|headshot/i.test(k);

function ImageField({ value, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/shop/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); onChange(data.url); }
    catch { /* ignore */ } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="h-14 w-14 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">{value ? <img src={imgUrl(value)} alt="" className="w-full h-full object-cover" /> : null}</div>
      <input className={input + " flex-1"} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL" />
      <button onClick={() => ref.current?.click()} disabled={busy} className="text-xs rounded-full border border-slate-300 px-3 py-2" data-testid="wm-image-upload">{busy ? "…" : "Upload"}</button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={up} />
    </div>
  );
}

function ScalarField({ k, value, onChange }) {
  if (k === "visible" || k === "featured") return null;
  if (isImgKey(k)) return <label className="text-sm block sm:col-span-2"><span className="capitalize">{k}</span><ImageField value={value} onChange={onChange} /></label>;
  const long = /copy|bio|subcopy|subheadline|desc|summary|disclaimer|philosophy|quote|paragraph/i.test(k);
  return (
    <label className="text-sm block sm:col-span-2">
      <span className="capitalize">{k.replace(/([A-Z])/g, " $1")}</span>
      {long ? <textarea className={input} rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} />
        : <input className={input} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />}
    </label>
  );
}

function ItemsEditor({ items, onChange, testid }) {
  const arr = items || [];
  const setItem = (i, obj) => onChange(arr.map((it, idx) => idx === i ? obj : it));
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= arr.length) return; const copy = [...arr]; [copy[i], copy[j]] = [copy[j], copy[i]]; onChange(copy); };
  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  const add = () => onChange([...arr, arr[0] ? Object.fromEntries(Object.keys(arr[0]).map((k) => [k, k === "visible" ? true : ""])) : { label: "", visible: true }]);
  return (
    <div className="space-y-3" data-testid={testid}>
      {arr.map((it, i) => (
        <div key={i} className="rounded-lg border border-slate-200 p-3 bg-slate-50" data-testid={`${testid}-row-${i}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-500">#{i + 1}</span>
            <div className="flex items-center gap-2">
              {"visible" in it && (
                <label className="flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={it.visible !== false} onChange={(e) => setItem(i, { ...it, visible: e.target.checked })} data-testid={`${testid}-visible-${i}`} /> Show</label>
              )}
              <button onClick={() => move(i, -1)} className="p-1 text-slate-400 hover:text-slate-700" data-testid={`${testid}-up-${i}`}><ChevronUp className="h-4 w-4" /></button>
              <button onClick={() => move(i, 1)} className="p-1 text-slate-400 hover:text-slate-700" data-testid={`${testid}-down-${i}`}><ChevronDown className="h-4 w-4" /></button>
              <button onClick={() => remove(i)} className="p-1 text-red-500" data-testid={`${testid}-del-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {Object.keys(it).filter((k) => k !== "featured").map((k) => <ScalarField key={k} k={k} value={it[k]} onChange={(v) => setItem(i, { ...it, [k]: v })} />)}
          </div>
        </div>
      ))}
      <button onClick={add} className="text-xs inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1.5" data-testid={`${testid}-add`}><Plus className="h-3.5 w-3.5" /> Add</button>
    </div>
  );
}

function StringsEditor({ items, onChange, testid }) {
  const arr = items || [];
  return (
    <div className="space-y-2" data-testid={testid}>
      {arr.map((f, i) => (
        <div key={i} className="flex gap-2">
          <textarea className={input + " flex-1 mt-0"} rows={2} value={f} onChange={(e) => onChange(arr.map((x, idx) => idx === i ? e.target.value : x))} />
          <button onClick={() => onChange(arr.filter((_, idx) => idx !== i))} className="text-red-500"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button onClick={() => onChange([...arr, ""])} className="text-xs inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1.5"><Plus className="h-3.5 w-3.5" /> Add line</button>
    </div>
  );
}

export default function ClinicWebsite() {
  const [pageKey, setPageKey] = useState("homepage");
  const cfg = PAGES[pageKey];
  const [content, setContent] = useState(null);
  const [open, setOpen] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [showPreview, setShowPreview] = useState(false);

  const load = async (key) => {
    const c = PAGES[key];
    setContent(null); setOpen(c.sections[0].key);
    const merge = key === "homepage" ? (_d, p) => mergeHome(p) : mergeContent;
    try { const { data } = await api.get(`/site-content/admin/${c.api}?mode=draft`); setContent(merge(c.defaults, data.content)); }
    catch { setContent(merge(c.defaults, null)); }
  };
  useEffect(() => { load(pageKey); /* eslint-disable-next-line */ }, [pageKey]);

  const setField = (sk, field, val) => setContent((c) => ({ ...c, [sk]: { ...c[sk], [field]: val } }));

  const saveDraft = async () => { setBusy(true); setMsg(""); try { await api.put(`/site-content/admin/${cfg.api}`, { content }); setMsg("Draft saved."); } catch { setMsg("Save failed"); } finally { setBusy(false); } };
  const publish = async () => { setBusy(true); setMsg(""); try { await api.post(`/site-content/admin/${cfg.api}/publish`, { content }); setMsg("Published live!"); } catch { setMsg("Publish failed"); } finally { setBusy(false); } };
  const resetDefaults = () => { if (!window.confirm("Reset this page's draft to KISSED defaults? (does not publish)")) return; setContent(pageKey === "homepage" ? mergeHome(null) : mergeContent(cfg.defaults, null)); setMsg("Draft reset to defaults — Save Draft & Publish to apply."); };

  if (!content) return <div className="min-h-screen bg-[#FBF8F2] p-10 text-sm text-slate-500">Loading…</div>;
  const previewUrl = `${process.env.REACT_APP_BACKEND_URL}${cfg.previewTo}`;

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <span className="font-display text-lg text-slate-900">KISSED Clinic OS · Website Manager</span>
          <div className="flex items-center gap-3">
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-600 inline-flex items-center gap-1 hover:underline" data-testid="wm-open-preview">Open preview <ExternalLink className="h-3.5 w-3.5" /></a>
            <button onClick={saveDraft} disabled={busy} className="rounded-full border border-purple-900 text-purple-900 text-sm px-4 py-1.5" data-testid="wm-save-draft">Save Draft</button>
            <button onClick={publish} disabled={busy} className="rounded-full bg-purple-900 text-white text-sm px-4 py-1.5" data-testid="wm-publish">Publish</button>
            <Link to="/admin" className="text-xs text-purple-900 hover:underline">← Admin Hub</Link>
          </div>
        </div>
      </header>

      {/* Page selector */}
      <div className="max-w-6xl mx-auto px-6 pt-5">
        <div className="flex flex-wrap gap-2" data-testid="wm-page-tabs">
          {Object.entries(PAGES).map(([k, p]) => (
            <button key={k} onClick={() => setPageKey(k)} className={`text-sm rounded-full px-4 py-1.5 ${pageKey === k ? "bg-purple-900 text-white" : "bg-white border border-slate-300 text-slate-600"}`} data-testid={`wm-page-${k}`}>{p.label}</button>
          ))}
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-6 py-6 grid lg:grid-cols-2 gap-6" data-testid="clinic-website-admin">
        <div>
          {msg && <div className="mb-4 text-sm text-purple-900 bg-purple-50 rounded-lg px-3 py-2" data-testid="wm-msg">{msg}</div>}
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-slate-500">Editing <b>{cfg.label}</b>. Changes save to a draft; click Publish to go live.</p>
            <button onClick={resetDefaults} className="text-xs text-slate-400 hover:text-red-500" data-testid="wm-reset-defaults">Reset to defaults</button>
          </div>

          {cfg.sections.map(({ key, label }) => {
            const sec = content[key] || {};
            const isOpen = open === key;
            const hasVisible = "visible" in sec;
            return (
              <div key={key} className="mb-3 rounded-xl border border-slate-200 bg-white" data-testid={`wm-section-${key}`}>
                <button onClick={() => setOpen(isOpen ? "" : key)} className="w-full flex items-center justify-between px-4 py-3" data-testid={`wm-toggle-${key}`}>
                  <span className="font-medium text-slate-800">{label}</span>
                  <span className="flex items-center gap-3">
                    {hasVisible && <span className={`text-[10px] uppercase rounded px-1.5 py-0.5 ${sec.visible === false ? "bg-slate-100 text-slate-400" : "bg-green-50 text-green-700"}`}>{sec.visible === false ? "Hidden" : "Visible"}</span>}
                    {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 border-t border-slate-100">
                    {hasVisible && (
                      <label className="flex items-center gap-2 text-sm my-3">
                        <input type="checkbox" checked={sec.visible !== false} onChange={(e) => setField(key, "visible", e.target.checked)} data-testid={`wm-visible-${key}`} />
                        Show this section
                      </label>
                    )}
                    <div className="grid sm:grid-cols-2 gap-2">
                      {Object.keys(sec).filter((k) => !Array.isArray(sec[k]) && k !== "visible").map((k) => <ScalarField key={k} k={k} value={sec[k]} onChange={(v) => setField(key, k, v)} />)}
                    </div>
                    {Object.keys(sec).filter((k) => Array.isArray(sec[k])).map((k) => (
                      <div key={k} className="mt-4">
                        <div className="text-xs font-semibold text-slate-500 uppercase mb-2">{k}</div>
                        {(sec[k].length === 0 || typeof sec[k][0] === "string")
                          ? <StringsEditor items={sec[k]} onChange={(v) => setField(key, k, v)} testid={`wm-list-${key}-${k}`} />
                          : <ItemsEditor items={sec[k]} onChange={(v) => setField(key, k, v)} testid={`wm-list-${key}-${k}`} />}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="hidden lg:block">
          <div className="sticky top-20">
            <div className="flex items-center gap-2 mb-2">
              <button onClick={() => { setShowPreview(true); setDevice("desktop"); }} className={`text-xs rounded-full px-3 py-1 border ${showPreview && device === "desktop" ? "bg-purple-900 text-white border-purple-900" : "border-slate-300 text-slate-600"}`} data-testid="wm-preview-desktop">Desktop</button>
              <button onClick={() => { setShowPreview(true); setDevice("mobile"); }} className={`text-xs rounded-full px-3 py-1 border ${showPreview && device === "mobile" ? "bg-purple-900 text-white border-purple-900" : "border-slate-300 text-slate-600"}`} data-testid="wm-preview-mobile">Mobile</button>
              <span className="text-xs text-slate-400">Save Draft first to refresh</span>
            </div>
            {showPreview ? (
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden mx-auto" style={{ width: device === "mobile" ? 390 : "100%", height: 620 }}>
                <iframe title="preview" src={previewUrl} className="w-full h-full" />
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-300 h-[620px] flex items-center justify-center text-sm text-slate-400">Click Desktop or Mobile to preview your draft</div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
